import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';

var animCSS = '\n\
@keyframes pp-fade-up {\n\
  from { opacity: 0; transform: translateY(24px); }\n\
  to { opacity: 1; transform: translateY(0); }\n\
}\n\
@keyframes pp-scale-in {\n\
  from { opacity: 0; transform: scale(0.85); }\n\
  to { opacity: 1; transform: scale(1); }\n\
}\n\
.pp-hidden { opacity: 0; }\n\
.pp-fade-up { animation: pp-fade-up 0.6s ease-out forwards; }\n\
.pp-scale-in { animation: pp-scale-in 0.5s ease-out forwards; }\n\
.pp-stagger-1 { animation-delay: 0s; }\n\
.pp-stagger-2 { animation-delay: 0.12s; }\n\
.pp-stagger-3 { animation-delay: 0.24s; }\n\
.pp-stagger-4 { animation-delay: 0.36s; }\n\
.pp-stagger-5 { animation-delay: 0.48s; }\n\
';

function Section(props) {
  var staggerClass = props.stagger ? ' pp-stagger-' + props.stagger : '';
  return (
    <div
      className={'pp-hidden tw-bg-white dark:tw-bg-[#141414] tw-rounded-2xl tw-border tw-border-[#e8e8e8] dark:tw-border-[#2a2a2a] tw-p-6 tw-mb-6' + staggerClass}
      style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
      data-anim="pp-fade-up"
    >
      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
        <div
          className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-lg tw-flex-shrink-0"
          style={{ backgroundColor: props.iconBg || 'rgba(201,168,76,0.1)', color: props.iconColor || '#c9a84c' }}
        >
          <i className={'fa fa-' + props.icon} />
        </div>
        <h2 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mt-0 tw-mb-0">
          {props.title}
        </h2>
      </div>
      <div className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-leading-relaxed">
        {props.children}
      </div>
    </div>
  );
}

class PrivacyPolicy extends Component {
  constructor(props) {
    super(props);
    this._observer = null;
    this.state = { showScrollTop: false };
    this._handleScroll = this._handleScroll.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this._setupObserver();
    window.addEventListener('scroll', this._handleScroll);
  }

  componentWillUnmount() {
    if (this._observer) {
      this._observer.disconnect();
    }
    window.removeEventListener('scroll', this._handleScroll);
  }

  _handleScroll() {
    var show = window.scrollY > 400;
    if (show !== this.state.showScrollTop) {
      this.setState({ showScrollTop: show });
    }
  }

  _setupObserver() {
    if (typeof IntersectionObserver === 'undefined') {
      var els = document.querySelectorAll('.pp-hidden');
      for (var i = 0; i < els.length; i++) {
        els[i].classList.remove('pp-hidden');
      }
      return;
    }

    var obs;
    obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var el = entry.target;
            var anim = el.getAttribute('data-anim') || 'pp-fade-up';
            el.classList.add(anim);
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -20px 0px' }
    );
    this._observer = obs;

    var self = this;
    setTimeout(function () {
      var els = document.querySelectorAll('.pp-hidden');
      for (var i = 0; i < els.length; i++) {
        self._observer.observe(els[i]);
      }
    }, 50);
  }

  render() {
    return (
      <div className="tw-min-h-screen">
        <style>{animCSS}</style>
        <Helmet>
          <title>Privacy Policy | Primal Marketplace</title>
          <meta name="description" content="Privacy Policy for Primal Marketplace. Learn how we collect, use, and protect your personal information." />
          <meta property="og:title" content="Privacy Policy | Primal Marketplace" />
          <meta property="og:description" content="Learn how Primal Marketplace handles your data and privacy." />
        </Helmet>

        {/* Hero */}
        <div className="tw-text-center tw-pt-10 tw-pb-8 tw-px-4 tw-max-w-2xl tw-mx-auto" style={{ animation: 'pp-fade-up 0.6s ease-out both' }}>
          <h1 className="tw-text-3xl md:tw-text-4xl tw-font-extrabold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-4 tw-leading-tight">
            Privacy Policy
          </h1>
          <p className="tw-text-lg tw-text-[#666] dark:tw-text-gray-400 tw-leading-relaxed tw-mb-0">
            Your privacy matters. Here's how we handle your data.
          </p>
          <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-mt-3">
            Last updated: February 2026
          </p>
        </div>

        <div className="tw-max-w-3xl tw-mx-auto tw-px-4 tw-pb-16">

          {/* 1. What We Collect */}
          <Section icon="database" title="1. What We Collect" stagger="1">
            <p className="tw-mb-3">We collect information you provide directly and information generated through your use of the Platform:</p>

            <p className="tw-mb-2"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Account Information</strong></p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-1 tw-mb-3">
              <li>Email address, username, and password</li>
              <li>Profile picture and display name</li>
              <li>Phone number (optional, for seller verification)</li>
            </ul>

            <p className="tw-mb-2"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Transaction Information</strong></p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-1 tw-mb-3">
              <li>Shipping and billing addresses</li>
              <li>Payment details (tokenized through Stripe — we never store full card numbers)</li>
              <li>PayPal email address (for sellers receiving payouts)</li>
              <li>Order history and transaction records</li>
            </ul>

            <p className="tw-mb-2"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Usage Information</strong></p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-1 tw-mb-3">
              <li>Pages visited, features used, and actions taken on the Platform</li>
              <li>Device information, browser type, and IP address</li>
              <li>Analytics data collected via Mixpanel (with your consent)</li>
              <li>Error and performance data collected via Sentry for platform stability</li>
            </ul>

            <p className="tw-mb-2"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Communication Data</strong></p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-1 tw-mb-3">
              <li>Direct messages between buyers and sellers</li>
              <li>Offer and counter-offer details (amounts, status, expiration)</li>
              <li>Buylist entries (cards wanted, quantities, target prices)</li>
            </ul>

            <p className="tw-mb-2"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Safety & Moderation Data</strong></p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-1 tw-mb-0">
              <li>Block and report records</li>
              <li>Account standing information (strikes, suspensions)</li>
              <li>System notifications and admin communications</li>
            </ul>
          </Section>

          {/* 2. How We Use It */}
          <Section icon="cogs" title="2. How We Use Your Information" stagger="2">
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-0">
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Process transactions</strong> — facilitate purchases, payouts, and escrow holds between buyers and sellers</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Send communications</strong> — order confirmations, shipping updates, password resets, and account verification emails</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Improve the Platform</strong> — analyze usage patterns to enhance features and user experience</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Prevent fraud</strong> — detect and prevent fraudulent activity, chargebacks, and policy violations</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Legal compliance</strong> — fulfill legal obligations, respond to legal requests, and enforce our Terms of Service</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Enable communication</strong> — facilitate buyer-seller messaging, offer negotiation, and buylist fulfillment</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Enforce safety</strong> — process user reports, maintain block lists, and administer the strike and suspension system</li>
            </ul>
          </Section>

          {/* 3. How We Protect It */}
          <Section icon="lock" title="3. How We Protect Your Data" stagger="3">
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-3">
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Encryption at rest:</strong> Sensitive data (shipping addresses, phone numbers, payment account IDs) is encrypted using AES-256-GCM before storage</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Password security:</strong> Passwords are managed by AWS Cognito, which handles secure hashing and verification. We never store or have access to your plain text password</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Encryption in transit:</strong> All data transmitted between your browser and our servers is encrypted via HTTPS/TLS</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">PCI Compliance:</strong> Credit card information is processed by Stripe, a PCI DSS Level 1 certified payment processor. We never see or store your full card number</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Partial card storage:</strong> For your order history, we store only the last 4 digits and card brand (e.g., "Visa ending in 4242")</li>
            </ul>
            <p className="tw-mb-0">
              While we take reasonable measures to protect your data, no method of electronic storage or transmission is 100% secure.
            </p>
          </Section>

          {/* 4. Third-Party Services */}
          <Section icon="share-alt" title="4. Third-Party Services" stagger="4">
            <p className="tw-mb-3">We share data with the following services to operate the Platform:</p>

            <div className="tw-space-y-3 tw-mb-0">
              <div className="tw-bg-[#f9f9f9] dark:tw-bg-[#1a1a1a] tw-rounded-lg tw-p-3 tw-border tw-border-[#eee] dark:tw-border-[#2a2a2a]">
                <p className="tw-mb-1"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Stripe</strong> — Payment processing</p>
                <p className="tw-mb-0 tw-text-xs">Receives: card details (tokenized), billing address, email. Used for processing payments and seller payouts via Stripe Connect.</p>
              </div>
              <div className="tw-bg-[#f9f9f9] dark:tw-bg-[#1a1a1a] tw-rounded-lg tw-p-3 tw-border tw-border-[#eee] dark:tw-border-[#2a2a2a]">
                <p className="tw-mb-1"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">PayPal</strong> — Payment processing & payouts</p>
                <p className="tw-mb-0 tw-text-xs">Receives: email, shipping address. PayPal may share your shipping address and email with us during checkout, which may be saved to your profile.</p>
              </div>
              <div className="tw-bg-[#f9f9f9] dark:tw-bg-[#1a1a1a] tw-rounded-lg tw-p-3 tw-border tw-border-[#eee] dark:tw-border-[#2a2a2a]">
                <p className="tw-mb-1"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Amazon Web Services (Cognito)</strong> — Authentication</p>
                <p className="tw-mb-0 tw-text-xs">Receives: email address, password. Used for secure account authentication, password management, and email verification.</p>
              </div>
              <div className="tw-bg-[#f9f9f9] dark:tw-bg-[#1a1a1a] tw-rounded-lg tw-p-3 tw-border tw-border-[#eee] dark:tw-border-[#2a2a2a]">
                <p className="tw-mb-1"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Amazon Web Services (SES)</strong> — Transactional email</p>
                <p className="tw-mb-0 tw-text-xs">Receives: email address. Used for sending order confirmations, shipping updates, and email verification.</p>
              </div>
              <div className="tw-bg-[#f9f9f9] dark:tw-bg-[#1a1a1a] tw-rounded-lg tw-p-3 tw-border tw-border-[#eee] dark:tw-border-[#2a2a2a]">
                <p className="tw-mb-1"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Mixpanel</strong> — Analytics (with consent)</p>
                <p className="tw-mb-0 tw-text-xs">Receives: anonymized usage data, page views, feature interactions. Only active when you consent to analytics cookies. You can opt out at any time.</p>
              </div>
              <div className="tw-bg-[#f9f9f9] dark:tw-bg-[#1a1a1a] tw-rounded-lg tw-p-3 tw-border tw-border-[#eee] dark:tw-border-[#2a2a2a]">
                <p className="tw-mb-1"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Sentry</strong> — Error monitoring</p>
                <p className="tw-mb-0 tw-text-xs">Receives: error reports, browser type, device info, and IP address when errors occur. Used to identify and fix bugs to improve platform stability. No personal content (messages, payment details) is captured. Always active to ensure platform reliability.</p>
              </div>
            </div>
          </Section>

          {/* 5. Cookies & Tracking */}
          <Section icon="desktop" title="5. Cookies & Local Storage" stagger="5">
            <p className="tw-mb-3">We use browser storage to operate the Platform:</p>

            <p className="tw-mb-2"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Essential (always active)</strong></p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-1 tw-mb-3">
              <li><code className="tw-text-xs tw-bg-[#f0f0f0] dark:tw-bg-[#222] tw-px-1 tw-rounded">primal_cognito_token</code> — Authentication session (localStorage)</li>
              <li><code className="tw-text-xs tw-bg-[#f0f0f0] dark:tw-bg-[#222] tw-px-1 tw-rounded">primal_user_profile</code> — Cached user profile (localStorage)</li>
              <li><code className="tw-text-xs tw-bg-[#f0f0f0] dark:tw-bg-[#222] tw-px-1 tw-rounded">primal_cart</code> — Shopping cart contents (localStorage)</li>
              <li><code className="tw-text-xs tw-bg-[#f0f0f0] dark:tw-bg-[#222] tw-px-1 tw-rounded">primal_theme</code> — Light/dark mode preference (localStorage)</li>
              <li><code className="tw-text-xs tw-bg-[#f0f0f0] dark:tw-bg-[#222] tw-px-1 tw-rounded">primal_cookie_consent</code> — Your cookie preference (localStorage)</li>
              <li>Session-scoped UI state (sessionStorage)</li>
            </ul>

            <p className="tw-mb-2"><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Analytics (opt-in)</strong></p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-1 tw-mb-0">
              <li>Mixpanel cookies for tracking page views and feature usage</li>
              <li>Only active if you accept analytics cookies via the consent banner</li>
              <li>You can change your preference at any time from the cookie banner</li>
            </ul>
          </Section>

          {/* 6. Your Rights */}
          <Section icon="user-circle" title="6. Your Rights">
            <p className="tw-mb-3">You have the following rights regarding your personal data:</p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-3">
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Access:</strong> You can view your personal information in your <Link to="/settings" className="tw-text-brand-gold hover:tw-underline">account settings</Link></li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Correction:</strong> You can update your profile information, email, and addresses at any time</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Deletion:</strong> You can delete your account from your settings page. This scrubs your personal data (name, email, addresses, phone) from our systems</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Opt-out of analytics:</strong> You can decline analytics cookies or withdraw consent at any time via the cookie consent banner</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Data portability:</strong> Contact us to request a copy of your personal data</li>
            </ul>
            <p className="tw-mb-0">
              To exercise any of these rights, email us at{' '}
              <a href="mailto:dattebayolabs@gmail.com" className="tw-text-brand-gold hover:tw-underline">dattebayolabs@gmail.com</a>.
            </p>
          </Section>

          {/* 7. Data Retention */}
          <Section icon="archive" title="7. Data Retention">
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-3">
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Active accounts:</strong> Personal data is retained as long as your account is active</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Deleted accounts:</strong> Personal information (name, email, addresses, phone, payment IDs) is scrubbed. The username is replaced with "Deleted User"</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Transaction records:</strong> Retained for legal and financial compliance even after account deletion. These records contain order amounts, dates, and partial card data (last 4 digits)</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Analytics data:</strong> Mixpanel data is retained per Mixpanel's retention policy and is anonymized</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Messages & conversations:</strong> Retained while both parties have active accounts. If one party deletes their account, messages remain visible to the other party but the deleted user's name is replaced with "Deleted User"</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Moderation records:</strong> Strike history, admin notes, and report records are retained for platform safety even after account deletion</li>
            </ul>
            <p className="tw-mb-0">
              We retain data only as long as necessary for the purposes outlined in this policy or as required by law.
            </p>
          </Section>

          {/* 8. Children's Privacy */}
          <Section icon="child" title="8. Children's Privacy">
            <p className="tw-mb-2">
              Primal Marketplace does not knowingly collect personal information from children under 13 years of age, in compliance with the Children's Online Privacy Protection Act (COPPA).
            </p>
            <p className="tw-mb-0">
              If we learn that we have collected data from a child under 13, we will delete that information promptly. If you believe a child has provided us with personal data, please contact us at{' '}
              <a href="mailto:dattebayolabs@gmail.com" className="tw-text-brand-gold hover:tw-underline">dattebayolabs@gmail.com</a>.
            </p>
          </Section>

          {/* 9. California Residents (CCPA) */}
          <Section icon="map-marker" title="9. California Residents (CCPA)">
            <p className="tw-mb-3">
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-3">
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">We do not sell your personal information.</strong> Primal Marketplace has never sold and will not sell your personal data to third parties.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Right to know:</strong> You can request details about the categories of personal information we collect and how it is used.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Right to delete:</strong> You can request deletion of your personal information (subject to legal exceptions for transaction records).</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Non-discrimination:</strong> We will not discriminate against you for exercising your privacy rights.</li>
            </ul>
            <p className="tw-mb-0">
              To submit a CCPA request, email{' '}
              <a href="mailto:dattebayolabs@gmail.com" className="tw-text-brand-gold hover:tw-underline">dattebayolabs@gmail.com</a>{' '}
              with the subject line "CCPA Request."
            </p>
          </Section>

          {/* 10. International Users */}
          <Section icon="globe" title="10. International Users">
            <p className="tw-mb-0">
              Primal Marketplace is operated in the United States. If you access the Platform from outside the US, your data will be transferred to and processed in the United States. By using the Platform, you consent to this transfer. We comply with applicable data protection laws including GDPR where relevant — EU/EEA residents may exercise their data rights by contacting us.
            </p>
          </Section>

          {/* 11. Changes to Policy */}
          <Section icon="pencil" title="11. Changes to This Policy">
            <p className="tw-mb-0">
              We may update this Privacy Policy from time to time. Material changes will be communicated via email or a prominent notice on the Platform. The "Last updated" date at the top of this page indicates when the policy was last revised. Your continued use after changes constitutes acceptance.
            </p>
          </Section>

          {/* 12. Contact */}
          <Section icon="envelope" title="12. Contact Us">
            <p className="tw-mb-2">
              If you have questions about this Privacy Policy or how we handle your data, please contact us:
            </p>
            <p className="tw-mb-0">
              <strong className="tw-text-[#1a1a1a] dark:tw-text-white">Email:</strong>{' '}
              <a href="mailto:dattebayolabs@gmail.com" className="tw-text-brand-gold hover:tw-underline">dattebayolabs@gmail.com</a>
            </p>
          </Section>

          {/* Bottom nav */}
          <div className="pp-hidden tw-text-center tw-mt-8" data-anim="pp-fade-up">
            <Link to="/terms" className="tw-text-brand-gold hover:tw-underline tw-text-sm tw-font-medium">
              &larr; Read our Terms of Service
            </Link>
          </div>

        </div>

        {/* Scroll to top button */}
        {this.state.showScrollTop && (
          <button
            onClick={function () { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="tw-fixed tw-bottom-6 tw-right-6 tw-w-10 tw-h-10 tw-rounded-full tw-bg-brand-gold tw-text-black tw-border-0 tw-cursor-pointer tw-z-40 tw-flex tw-items-center tw-justify-center mobile-tab-offset-btn"
            style={{ boxShadow: '0 4px 16px rgba(201, 168, 76, 0.3)', animation: 'hp-fadeUp 0.3s ease forwards' }}
            aria-label="Scroll to top"
          >
            <svg className="tw-w-6 tw-h-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    );
  }
}

export default PrivacyPolicy;
